export type ShareOrDownloadInput = {
  blob: Blob
  filename: string
  title?: string
  text?: string
}

export type ShareOrDownloadOutcome = 'shared' | 'downloaded' | 'cancelled'

type ShareNavigator = Navigator & {
  canShare?: (data?: ShareData) => boolean
  share?: (data?: ShareData) => Promise<void>
}

/**
 * Creates an object URL, triggers a download via a temporary anchor, then revokes the URL.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.rel = 'noopener'
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

/**
 * Tries Web Share with a File when supported; otherwise downloads.
 * Returns 'cancelled' when the user dismisses the share sheet.
 */
export async function shareOrDownload(input: ShareOrDownloadInput): Promise<ShareOrDownloadOutcome> {
  const shareData = buildShareData(input)
  const shared = await tryShare(shareData)
  if (shared === 'shared' || shared === 'cancelled') return shared

  downloadBlob(input.blob, input.filename)
  return 'downloaded'
}

function buildShareData(input: ShareOrDownloadInput): ShareData {
  // Strip any `;codecs=...` suffix: iOS Safari's navigator.canShare rejects a
  // File whose type carries codec params, so we hand it the bare container
  // (video/mp4). Default to mp4 since the POC only records shareable MP4.
  const containerType = input.blob.type.split(';')[0]?.trim() || 'video/mp4'
  const file = new File([input.blob], input.filename, {
    type: containerType,
  })
  return {
    files: [file],
    title: input.title,
    text: input.text,
  }
}

async function tryShare(shareData: ShareData): Promise<ShareOrDownloadOutcome | null> {
  const nav = typeof navigator !== 'undefined' ? (navigator as ShareNavigator) : null
  if (!canShareFiles(nav, shareData) || !nav?.share) return null

  try {
    await nav.share(shareData)
    return 'shared'
  } catch (error) {
    if (isAbortError(error)) return 'cancelled'
    return null
  }
}

function canShareFiles(nav: ShareNavigator | null, shareData: ShareData): boolean {
  return !!nav?.share && typeof nav.canShare === 'function' && nav.canShare(shareData)
}

function isAbortError(error: unknown): boolean {
  return (
    !!error &&
    typeof error === 'object' &&
    'name' in error &&
    (error as { name?: string }).name === 'AbortError'
  )
}
